import classNames from 'classnames'
import {Button} from 'antd'
import useTranslate from 'modules/local/useTranslate'
import React, {useState} from 'react'
import {useFormikContext} from 'formik'
import {IoChevronDown} from 'react-icons/io5'
import {NullAsDefault} from 'views/Shared'

// Pattern form CRUD "hiện đại" dùng CHUNG toàn app - Section nền màu theo độ ưu tiên (highlight =
// trọng tâm bắt buộc, neutral = còn lại - chỉ 2 tông, tránh "màu sắc quá"), Field label rõ + báo
// lỗi, ChipGroup cho field chọn 1/nhiều trong danh sách cố định ít lựa chọn, FormActions Hủy/Submit
// có border-t. Thay cho FieldsFactory/formSchema cũ (phẳng, khó custom style/phân nhóm).
// Đã áp dụng: ProfileForm.js, ParishForm.js. Xem docs/ui/form-layout.md.

const SECTION_THEME = {
    neutral: 'bg-background-50',
    highlight: 'bg-rose-50',
}

// `collapsible`/`defaultCollapsed`: Section thu gọn được, chỉ hiện title/subtitle + mũi tên, bấm để
// mở - dùng cho khối thông tin phụ ít khi cần sửa (vd "Thông tin liên hệ"), tránh form dài rợp mắt
// ngay từ đầu. Field bên trong collapsed vẫn giữ nguyên value/state (chỉ ẩn bằng điều kiện render,
// không unmount Formik field nào cả vì Section không tự quản value - an toàn khi mở lại).
export const Section = ({icon: Icon, title, subtitle, theme = 'neutral', collapsible = false, defaultCollapsed = false, children}) => {
    const [collapsed, setCollapsed] = useState(collapsible && defaultCollapsed)
    const isCollapsed = collapsible && collapsed
    return (
        <div className={classNames('flex flex-col p-3 rounded-xl', SECTION_THEME[theme] || SECTION_THEME.neutral)}>
            <div
                onClick={collapsible ? () => setCollapsed((c) => !c) : undefined}
                className={classNames('flex items-start gap-2', collapsible && 'cursor-pointer', title && 'mb-3')}>
                {Icon && <Icon size={13} className="text-rose-800 flex-shrink-0"/>}
                {title && (
                    <div className="flex flex-col flex-1 min-w-0">
                        <span className="text-2xs font-bold uppercase tracking-widest text-gray-500">{title}</span>
                        {subtitle && <span className="text-2xs text-color-300 italic tracking-wider flex items-center justify-between gap-2">{subtitle}</span>}
                    </div>
                )}
                {collapsible && (
                    <IoChevronDown
                        size={13}
                        className={classNames('text-color-300 flex-shrink-0 transition-transform', !isCollapsed && 'rotate-180')}
                    />
                )}
            </div>
            {!isCollapsed && <div className={classNames("flex flex-col gap-3")}>{children}</div>}
        </div>
    )
}

// `hint`: dòng mô tả nhỏ dưới input - giải thích field có luật/hệ quả người dùng không đoán được
// (vd "chọn xong sẽ copy toàn bộ môn", "email trùng tài khoản sẽ dùng để liên kết"...). Field
// hiển nhiên (Tên, Email...) thì KHÔNG cần, thêm chỉ làm form dài + khó quét.
export const Field = ({label, required, error, hint, children}) => (
    <div className="flex flex-col gap-1">
        {label && (
            <span className="text-xs font-medium text-color-200 tracking-wider">
                {label}{required && <span style={{fontSize: 14}} className="text-rose-600 ml-0.5">*</span>}
            </span>
        )}
        {children}
        {hint && (
            <span style={{marginTop: 4, fontSize: 11}} className="text-color-400 italic leading-snug">
                {hint}
            </span>
        )}
        {/*{error && <span className="text-2xs text-rose-600 italic">{error}</span>}*/}
        {error && (
            <span
                style={{marginTop: 4, fontSize: 11}}
                className={classNames("text-rose-600 italic error-message", error && 'error-message-show')}>
                {error}
            </span>
        )}
    </div>
)

// `options`: [{label, value}]. `multiple`: mảng giá trị (multi-select, toggle độc lập từng chip)
// hay giá trị đơn (single-select, bấm lại chip đang active -> `unselectValue`). `required`: field
// bắt buộc luôn phải có giá trị (vd giới tính) - bấm lại chip đang active thì giữ nguyên, KHÔNG cho
// bỏ chọn về `unselectValue`/null. Thiếu cờ này thì chip mặc định hiện sẵn (vd "Nam") khi bị bấm lại
// (kể cả vô tình) sẽ âm thầm biến value thành null mà UI không cảnh báo gì - đã gặp thực tế.
export const ChipGroup = ({options = [], value, onChange, multiple = false, unselectValue = null, required = false}) => {
    const t = useTranslate()
    const selected = multiple ? (Array.isArray(value) ? value : []) : value
    const isActive = (v) => multiple ? selected.includes(v) : selected === v
    const toggle = (v) => {
        if (multiple) {
            onChange(selected.includes(v) ? selected.filter((e) => e !== v) : [...selected, v])
        } else {
            if (required && isActive(v)) return
            onChange(isActive(v) ? unselectValue : v)
        }
    }
    return (
        <div className="flex flex-wrap gap-2 transition-all duration-300">
            {options.map(({label, value: v, disabled}) => {
                const active = isActive(v)
                return (
                    <button
                        disabled={disabled}
                        type="button"
                        key={v}
                        onClick={() => {
                            if (disabled) return
                            toggle(v)
                        }}
                        style={{ minHeight: 32 }}
                        className={classNames(
                            'px-3 py-1 flex flex-center rounded-lg text-xs font-semibold border leading-tight transition-all duration-300',
                            disabled
                                ? 'bg-gray-50 border-color-100 text-color-200 cursor-not-allowed' :
                                active
                                    ? 'bg-rose-700 border-rose-700 text-white'
                                    : 'bg-white border-color-100 text-color-300 hover:border-rose-300 hover:text-rose-700'
                        )}>
                        {t(label)}
                    </button>
                )
            })}
        </div>
    )
}

// Factory tạo tab "1 field lớn" (thường SlateEditor) cho ActionModalWrapper - trả về component Ở
// MODULE SCOPE (gọi 1 lần, reference cố định) nên không dính bẫy remount-mất-focus (component tab
// phải là reference ổn định, không định nghĩa inline trong render - xem
// docs/ui/design-principles.md "Pattern: Inline CRUD trong list"). Field tự đọc live values qua
// useFormikContext() bên trong thay vì nhận qua props/closure.
export const createSlateTab = (fieldName, Input, Icon, titleKey) => () => {
    const t = useTranslate()
    const {values, setFieldValue} = useFormikContext()
    const onChange = (e) => setFieldValue(e.target.name, e.target.value)
    return (
        <Section icon={Icon} title={t(titleKey)} subtitle={t('optional')}>
            <Input name={fieldName} value={values[fieldName]} onChange={onChange}/>
        </Section>
    )
}

export const FormActions = ({onCancel = NullAsDefault, submitText = 'create', isSubmitting, isValid}) => {
    const t = useTranslate()
    return (
        <div className="flex items-center justify-end gap-2 pt-4 mt-1 border-t border-color-50">
            <span
                onClick={isSubmitting ? undefined : onCancel}
                className="mr-2 font-semibold text-sm text-color-300 hover:text-color-000 cursor-pointer">
                {t('cancel')}
            </span>
            <Button
                type="primary"
                htmlType="submit"
                loading={isSubmitting}
                disabled={!isValid}
                className="font-semibold button-rounded-lg">
                {t(submitText)}
            </Button>
        </div>
    )
}
